/**
 * Earned Wage Access — an employee drawing part of the wages they have ALREADY EARNED this cycle
 * before payday, recovered from that cycle's pay.
 *
 * THE SERVER DECIDES. `getEwaEligibility` is what the withdrawal dialog shows and
 * `requestEwaWithdrawal` re-derives every figure itself: the amount is checked against a fresh
 * eligibility calculation inside a locked transaction, never against anything the client computed
 * or displayed. Nothing about the cap, the fee, or what is left in the treasury is hardcoded in any
 * client.
 *
 * THE ROW IS THE RESERVATION. A request creates its withdrawal row (status `reserved`) in the same
 * locked transaction that checked eligibility, so simultaneous requests cannot both see the same
 * headroom. That row counts against the employee's limit and the treasury pool from then on —
 * through the payout, and if the payout's outcome is unknown. Only a definitive `failed` releases
 * it. A timeout is `unknown` and is reconciled by a person; it is never retried as a fresh payment.
 *
 * NO REAL RAIL. The only provider that exists is the built-in `sandbox`, which follows the real
 * request → reserve → payout → callback path and moves no money; every response says so
 * (`isSandbox`). A partner name in settings without an adapter behind it is `provider_not_connected`.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  ewaRecoveryStatusSchema,
  ewaWithdrawalStatusSchema,
  idempotencyKeySchema,
  instantSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();
const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });

export const EWA_UNAVAILABLE_REASONS = [
  'ewa_disabled',
  'provider_not_connected',
  'no_current_cycle',
  'no_salary_structure',
  'currency_mismatch',
  'no_bank_account',
  'nothing_accrued',
  /** Wages have been earned, but the ceiling or the treasury pool is already fully used. */
  'limit_reached',
] as const;
export const ewaUnavailableReasonSchema = z.enum(EWA_UNAVAILABLE_REASONS);
export type EwaUnavailableReason = z.infer<typeof ewaUnavailableReasonSchema>;

export const ewaEligibilitySchema = z.object({
  employmentId: uuidSchema,
  /** True only when a withdrawal could be requested right now. */
  available: z.boolean(),
  /** Why not, when `available` is false — always one of a fixed set the UI can explain. */
  unavailableReason: ewaUnavailableReasonSchema.nullable(),
  providerStatus: z.enum(['sandbox', 'not_connected']),
  currency: currencySchema.nullable(),
  /** Wages earned so far this cycle, from salary and recorded attendance. Null when not computable. */
  accruedWages: decimalStringSchema.nullable(),
  /** The policy ceiling on what may be drawn against this cycle. */
  policyCeiling: decimalStringSchema.nullable(),
  /** Already drawn against this cycle and not yet recovered, including payouts still in flight. */
  alreadyDrawn: decimalStringSchema.nullable(),
  /** Of `alreadyDrawn`, the part whose payout is not yet confirmed (pending or unknown). */
  inFlight: decimalStringSchema.nullable(),
  maxAvailable: decimalStringSchema.nullable(),
  limitedBy: z.enum(['policy', 'treasury', 'nothing_accrued']).nullable(),
  /** Configured fee per withdrawal, deducted from the payout. "0" when none. */
  feeAmount: decimalStringSchema.nullable(),
  /** The payroll cycle this is recovered from. */
  recoveryCycle: z
    .object({ id: uuidSchema, label: z.string(), payDate: dateOnlySchema })
    .nullable(),
  /** Where the money would go — the employee's own account on file, masked. */
  destination: z.object({ bankName: z.string().nullable(), accountLast4: z.string() }).nullable(),
  /** The day attendance was read up to (yesterday in the entity's timezone). */
  asOf: dateOnlySchema.nullable(),
});
export type EwaEligibility = z.infer<typeof ewaEligibilitySchema>;

export const ewaWithdrawalSchema = z.object({
  id: uuidSchema,
  reference: z.string(),
  employmentId: uuidSchema,
  employeeName: z.string(),
  employeeCode: z.string().nullable(),
  cycleId: uuidSchema,
  cyclePeriodLabel: z.string(),
  currency: currencySchema,
  amount: decimalStringSchema,
  fee: decimalStringSchema,
  /** What actually reaches the employee: `amount − fee`. They still owe back `amount`. */
  netPayout: decimalStringSchema,
  status: ewaWithdrawalStatusSchema,
  provider: z.string(),
  /** True for the simulator — no real money moved, whatever the status says. */
  isSandbox: z.boolean(),
  providerReference: z.string().nullable(),
  failureReason: z.string().nullable(),
  recoveryStatus: ewaRecoveryStatusSchema,
  requestedAt: instantSchema,
  disbursedAt: instantSchema.nullable(),
  version: z.number().int().positive(),
});
export type EwaWithdrawal = z.infer<typeof ewaWithdrawalSchema>;

export const requestEwaWithdrawalBodySchema = z.object({
  amount: decimalStringSchema,
});

export const listEwaWithdrawalsQuerySchema = paginationQuerySchema.extend({
  /** `mine`: the caller's own. `all`: needs `loan:manage`, limited to the entities they manage. */
  scope: z.enum(['mine', 'all']).default('mine'),
  status: ewaWithdrawalStatusSchema.optional(),
});

/**
 * A person settling a withdrawal whose outcome the system could not determine, after checking with
 * the provider. Mandatory note — it is the audit record for having decided the money's fate by hand.
 */
export const reconcileEwaWithdrawalBodySchema = z.object({
  outcome: z.enum(['disbursed', 'failed']),
  providerReference: z.string().trim().max(120).optional(),
  note: z.string().trim().min(1).max(500),
});

const withdrawalParams = z.object({ withdrawalId: uuidSchema });

export const payrollEwaContract = c.router(
  {
    getEwaEligibility: {
      method: 'GET',
      path: '/eligibility',
      responses: {
        200: ewaEligibilitySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'What the caller could draw right now, worked out on the server',
      metadata: routeMeta('payroll.ewa:request'),
    },

    requestEwaWithdrawal: {
      method: 'POST',
      path: '/withdrawals',
      headers: idempotencyHeadersSchema,
      body: requestEwaWithdrawalBodySchema,
      responses: {
        201: ewaWithdrawalSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** Not available (see eligibility), or the amount is more than can be drawn. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Reserve and pay out an amount — checked against a fresh eligibility calculation',
      metadata: routeMeta('payroll.ewa:request'),
    },

    listEwaWithdrawals: {
      method: 'GET',
      path: '/withdrawals',
      query: listEwaWithdrawalsQuerySchema,
      responses: {
        200: paginatedSchema(ewaWithdrawalSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Withdrawal history — your own, or everyone’s you manage',
      metadata: routeMeta(null),
    },

    reconcileEwaWithdrawal: {
      method: 'POST',
      path: '/withdrawals/:withdrawalId/reconcile',
      pathParams: withdrawalParams,
      headers: idempotencyHeadersSchema,
      body: reconcileEwaWithdrawalBodySchema,
      responses: {
        200: ewaWithdrawalSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** The withdrawal is not `unknown` or `payout_pending` — there is nothing to reconcile. */
        409: errorBodySchema,
      },
      summary: 'Settle a payout whose outcome was unknown, after checking with the provider',
      metadata: routeMeta(null),
    },
  },
  { pathPrefix: '/payroll/ewa' },
);
